import React, { ReactNode, createContext, memo, useContext, useState } from 'react'

import { Address, Deal } from '~/interfaces/types'
import { omitTypename } from '~/utils/omit-typename'

interface EcommerceProviderProps {
  value: {
    address: Address
    deals: Deal[]
    comparisonId: string
    currentProvider?: string
    list: string
    postcode?: string
    show10MoreClicked: boolean
    sort: string
    hasUspAnimated?: boolean
    metadata?: {
      dimension94: string | undefined
      page: string
    }
  }
  children: ReactNode
}

interface EcommerceContextData {
  data?: {
    comparisonId: string
    currentProvider?: string
    sort: string
    list: string
    postcode?: string
    metadata?: {
      dimension94: string | undefined
      page: string
    }
  }
  getImpressionId?: (id: string) => string
}

const haveDealsChanged = (deals1: Deal[], deals2: Deal[]) =>
  JSON.stringify(deals1, omitTypename) !== JSON.stringify(deals2, omitTypename)

const EcommerceContext = createContext<EcommerceContextData>({})

const EcommerceProvider = ({
  value: { comparisonId, currentProvider, list, postcode, show10MoreClicked, sort, metadata },
  children
}: EcommerceProviderProps) => {
  const [listLoadedId] = useState(comparisonId)

  const getComparisonId = () => (show10MoreClicked ? listLoadedId : comparisonId)

  const getImpressionId = (id: string) => `${getComparisonId()}-${id}`

  const data = {
    comparisonId: getComparisonId(),
    currentProvider,
    sort,
    list,
    postcode,
    metadata
  }

  return (
    <EcommerceContext.Provider value={{ data, getImpressionId }}>
      <div>{children}</div>
    </EcommerceContext.Provider>
  )
}

const isEqual = ({ value: prevValue }: EcommerceProviderProps, { value: nextValue }: EcommerceProviderProps) => {
  const isPostCodeEqual = prevValue.postcode === nextValue.postcode
  const isAddressEqual = (prevValue.address || {}).udprn === (nextValue.address || {}).udprn
  const areDealsEqual = !haveDealsChanged(prevValue.deals, nextValue.deals)
  const hasUspAnimatedEqual = prevValue.hasUspAnimated === nextValue.hasUspAnimated

  return isPostCodeEqual && isAddressEqual && areDealsEqual && hasUspAnimatedEqual
}

const EcommerceProviderMemo = memo(EcommerceProvider, isEqual)

const useEcommerceContext = () => {
  const ecommerceContext = useContext(EcommerceContext)

  return ecommerceContext
}

export { EcommerceProviderMemo as EcommerceProvider, useEcommerceContext }
